import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Upload, Trash2, Loader2, FileText, ToggleLeft } from "lucide-react";

interface StrategyResource {
  id: string;
  title: string;
  description: string | null;
  file_name: string;
  file_path: string;
  content_text: string | null;
  is_active: boolean;
  created_at: string;
}

const AdminResourceManager = () => {
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [resources, setResources] = useState<StrategyResource[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [contentText, setContentText] = useState("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  useEffect(() => {
    fetchResources();
  }, []);

  const fetchResources = async () => {
    setIsLoading(true);
    const { data, error } = await supabase
      .from("strategy_resources")
      .select("*")
      .order("created_at", { ascending: false });

    if (error) {
      toast({ title: "Error", description: "Failed to load resources", variant: "destructive" });
    } else {
      setResources((data as StrategyResource[]) || []);
    }
    setIsLoading(false);
  };

  const handleUpload = async () => {
    if (!selectedFile || !title.trim()) {
      toast({ title: "Missing fields", description: "Title and file are required", variant: "destructive" });
      return;
    }

    setIsUploading(true);
    try {
      const filePath = `${Date.now()}_${selectedFile.name}`;

      const { error: uploadError } = await supabase.storage
        .from("resource-documents")
        .upload(filePath, selectedFile);

      if (uploadError) throw uploadError;

      const { data: userData } = await supabase.auth.getUser();

      const { error: insertError } = await supabase
        .from("strategy_resources")
        .insert({
          title: title.trim(),
          description: description.trim() || null,
          file_path: filePath,
          file_name: selectedFile.name,
          content_text: contentText.trim() || null,
          uploaded_by: userData.user?.id,
        } as any);

      if (insertError) throw insertError;

      toast({ title: "Uploaded!", description: "Resource added successfully" });
      setTitle("");
      setDescription("");
      setContentText("");
      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      fetchResources();
    } catch (error) {
      console.error("Upload error:", error);
      toast({ title: "Error", description: error instanceof Error ? error.message : "Upload failed", variant: "destructive" });
    } finally {
      setIsUploading(false);
    }
  };

  const toggleActive = async (id: string, currentState: boolean) => {
    const { error } = await supabase
      .from("strategy_resources")
      .update({ is_active: !currentState } as any)
      .eq("id", id);

    if (error) {
      toast({ title: "Error", description: "Failed to update", variant: "destructive" });
    } else {
      setResources(prev => prev.map(r => r.id === id ? { ...r, is_active: !currentState } : r));
    }
  };

  const deleteResource = async (id: string, filePath: string) => {
    const { error: storageError } = await supabase.storage
      .from("resource-documents")
      .remove([filePath]);

    if (storageError) console.warn("Storage delete warning:", storageError);

    const { error } = await supabase
      .from("strategy_resources")
      .delete()
      .eq("id", id);

    if (error) {
      toast({ title: "Error", description: "Failed to delete", variant: "destructive" });
    } else {
      setResources(prev => prev.filter(r => r.id !== id));
      toast({ title: "Deleted", description: "Resource removed" });
    }
  };

  return (
    <div className="space-y-6">
      <Card className="p-8 shadow-lg">
        <h2 className="text-2xl font-bold mb-6">Upload Reference Resource</h2>
        <p className="text-sm text-muted-foreground mb-4">
          Upload PDF documents with gamification knowledge. Paste the key content below so the AI can reference it when generating strategies.
        </p>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="res-title">Title *</Label>
            <Input
              id="res-title"
              placeholder="e.g. Octalysis Deep Dive"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="res-desc">Description (optional)</Label>
            <Input
              id="res-desc"
              placeholder="Brief description of this resource"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="res-file">PDF File *</Label>
            <Input
              id="res-file"
              ref={fileInputRef}
              type="file"
              accept=".pdf"
              onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="res-content">
              Key Content Text (paste the important text the AI should reference)
            </Label>
            <Textarea
              id="res-content"
              placeholder="Paste the key excerpts, frameworks, or insights from this document that the AI should use when generating gamification strategies..."
              value={contentText}
              onChange={(e) => setContentText(e.target.value)}
              rows={8}
            />
            <p className="text-xs text-muted-foreground">
              The AI will include this text in its prompt context. Keep it focused on the most valuable insights.
            </p>
          </div>

          <Button
            onClick={handleUpload}
            disabled={isUploading || !selectedFile || !title.trim()}
            className="w-full bg-gradient-to-r from-primary to-accent hover:opacity-90"
          >
            {isUploading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Uploading...
              </>
            ) : (
              <>
                <Upload className="w-4 h-4 mr-2" />
                Upload Resource
              </>
            )}
          </Button>
        </div>
      </Card>

      <Card className="p-8 shadow-lg">
        <h2 className="text-2xl font-bold mb-6">
          Uploaded Resources ({resources.length})
        </h2>

        {isLoading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : resources.length === 0 ? (
          <p className="text-muted-foreground text-center py-8">
            No resources uploaded yet. Upload your first reference document above.
          </p>
        ) : (
          <div className="space-y-4">
            {resources.map((resource) => (
              <div
                key={resource.id}
                className={`flex items-start gap-4 p-4 rounded-lg border ${
                  resource.is_active ? "border-primary/20 bg-primary/5" : "border-muted bg-muted/30 opacity-60"
                }`}
              >
                <FileText className="w-8 h-8 text-primary flex-shrink-0 mt-1" />
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold">{resource.title}</h3>
                  {resource.description && (
                    <p className="text-sm text-muted-foreground">{resource.description}</p>
                  )}
                  <p className="text-xs text-muted-foreground mt-1">
                    {resource.file_name} · {resource.content_text ? `${resource.content_text.length} chars of content` : "No text content"}
                  </p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={resource.is_active}
                      onCheckedChange={() => toggleActive(resource.id, resource.is_active)}
                    />
                    <span className="text-xs text-muted-foreground w-8">
                      {resource.is_active ? "On" : "Off"}
                    </span>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => deleteResource(resource.id, resource.file_path)}
                    className="text-destructive hover:text-destructive"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
};

export default AdminResourceManager;
